import { IMainTableColumn } from "components/MainTable/MainTableInterfaces";
import { ICorporateDetailRow } from "../CorporationDetailsInterfaces";
import { useTranslation } from "react-i18next";
import { useCallback, useMemo } from "react";
import dayjs from "dayjs";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import MainSwitch from "components/MainSwitch/MainSwitch.view";
import { IOption } from "components/TableMenu/TableMenuInterfaces";
import { TableMenu } from "components";
import { useStyles } from "../CorporationDetails.styles";

interface ICorporationsDetailsColumns extends Omit<IMainTableColumn, "render"> {
    render: (row: ICorporateDetailRow) => string | JSX.Element | number | boolean | undefined;
}

export const useCorporationsDetailsColumns = (
    onAspenPlusToggleClick: (row: ICorporateDetailRow, enable: boolean) => void,
    onClusterToggleClick: (row: ICorporateDetailRow, enable: boolean) => void,
    onMillEnabledToggleClick: (row: ICorporateDetailRow, enable: boolean) => void,
    onDedicatedLocalserverToggleClick: (row: ICorporateDetailRow, enable: boolean) => void,
    isCorporateAspenEnabled: boolean,
): ICorporationsDetailsColumns[] => {
    const { t } = useTranslation();
    const classes = useStyles();

    const renderClusterOptions = useCallback(
        (row: ICorporateDetailRow) => {
            const isEnabled = row.clusterEnabled ?? false;
            return {
                name: isEnabled
                    ? t("setup_corporation.corporation_mills.deactivate")
                    : t("setup_corporation.corporation_mills.activate"),
                callback: () => onClusterToggleClick(row, !isEnabled),
                customStyles: { fontWeight: 600 },
            };
        },
        [onClusterToggleClick, t],
    );

    const disabledAspenTooltip = t("setup_corporation.corporation_mills.disabled_aspen_corporation_tooltip");
    const disabledLocalserverTooltip = t("setup_corporation.corporation_mills.disabled_localserver_aspen_tooltip");
    const disabledAspenPlusTooltip = t("setup_corporation.corporation_mills.disabled_aspen_plus_corporation_tooltip");

    const renderAspenPlusOptions = useCallback(
        (row: ICorporateDetailRow) => {
            const isEnabled = row.aspenPlusEnabled ?? false;
            return {
                name: isEnabled
                    ? t("setup_corporation.corporation_mills.deactivate_aspen_plus")
                    : t("setup_corporation.corporation_mills.activate_aspen_plus"),
                callback: () => onAspenPlusToggleClick(row, !isEnabled),
                customStyles: { fontWeight: 600 },
                disabled: !isCorporateAspenEnabled || !row.enabled,
                disabledTooltip: !isCorporateAspenEnabled || !row.enabled ? disabledAspenPlusTooltip : undefined,
            };
        },
        [onAspenPlusToggleClick, t, isCorporateAspenEnabled, disabledAspenPlusTooltip],
    );

    const tableColumns = useMemo(
        (): ICorporationsDetailsColumns[] => [
            {
                id: "name",
                label: t("setup_corporation.corporation_mills.table_name"),
                hideSortIcon: true,
                render: (row) => row.name,
            },
            {
                id: "location",
                label: t("setup_corporation.corporation_mills.table_location"),
                hideSortIcon: true,
                render: (row) => row.location,
            },
            {
                id: "division",
                label: t("setup_corporation.corporation_mills.table_division"),
                hideSortIcon: true,
                render: (row) => (row.division ? row.division : "N/A"),
            },
            {
                id: "region",
                label: t("setup_corporation.corporation_mills.table_region"),
                hideSortIcon: true,
                render: (row) => (row.region ? row.region : "N/A"),
            },
            {
                id: "admin",
                label: t("setup_corporation.corporation_mills.table_admin"),
                hideSortIcon: true,
                render: (row) => row.admin,
            },
            {
                id: "date",
                label: t("setup_corporation.corporation_mills.table_date_added"),
                hideSortIcon: true,
                render: (row) => dayjs(row.date).format("MM, DD, YYYY"),
            },
            {
                id: "enabled",
                label: t("setup_corporation.corporation_mills.table_enabled"),
                hideSortIcon: true,
                render: (row) => {
                    const switchEl = (
                        <MainSwitch
                            checked={row.enabled}
                            disabled={!isCorporateAspenEnabled}
                            onChange={() => onMillEnabledToggleClick(row, !row.enabled)}
                        />
                    );
                    return !isCorporateAspenEnabled ? (
                        <Tooltip
                            PopperProps={{ className: classes.tooltip }}
                            title={<Typography className={classes.tooltipTexts}>{disabledAspenTooltip}</Typography>}
                        >
                            <span style={{ display: "inline-flex" }}>{switchEl}</span>
                        </Tooltip>
                    ) : (
                        switchEl
                    );
                },
            },
            {
                id: "dedicated_localserver",
                label: t("setup_corporation.corporation_mills.dedicated_localserver"),
                hideSortIcon: true,
                render: (row) => {
                    const switchEl = (
                        <MainSwitch
                            checked={row.dedicatedLocalserver ?? false}
                            disabled={!isCorporateAspenEnabled || !row.enabled}
                            onChange={() =>
                                onDedicatedLocalserverToggleClick(row, !(row.dedicatedLocalserver ?? false))
                            }
                        />
                    );
                    return !isCorporateAspenEnabled || !row.enabled ? (
                        <Tooltip
                            PopperProps={{ className: classes.tooltip }}
                            title={
                                <Typography className={classes.tooltipTexts}>{disabledLocalserverTooltip}</Typography>
                            }
                        >
                            <span style={{ display: "inline-flex" }}>{switchEl}</span>
                        </Tooltip>
                    ) : (
                        switchEl
                    );
                },
            },
            {
                id: "account_status",
                label: t("setup_corporation.corporation_mills.account_status"),
                hideSortIcon: true,
                render: (row): any => (
                    <span className={classes.statusButton}>
                        {row.clusterEnabled ? t("setup_corporation.active") : t("setup_corporation.inactive")}
                    </span>
                ),
            },
            {
                id: "aspen_plus",
                label: t("setup_corporation.corporation_mills.aspen_plus"),
                hideSortIcon: true,
                render: (row): any => (
                    <span className={classes.statusButton}>
                        {row.aspenPlusEnabled ? t("setup_corporation.active") : t("setup_corporation.inactive")}
                    </span>
                ),
            },
            {
                id: "options",
                label: "",
                hideSortIcon: true,
                render: (row) => {
                    const options: IOption[] = [{ ...renderClusterOptions(row) }, { ...renderAspenPlusOptions(row) }];
                    return <TableMenu options={options} />;
                },
            },
        ],
        [
            t,
            classes.statusButton,
            classes.tooltip,
            classes.tooltipTexts,
            renderClusterOptions,
            renderAspenPlusOptions,
            onMillEnabledToggleClick,
            onDedicatedLocalserverToggleClick,
            isCorporateAspenEnabled,
            disabledAspenTooltip,
            disabledLocalserverTooltip,
        ],
    );

    return tableColumns;
};
